import "./TextField.scss";
import Control from "../../Control";
import Delegate from "../../Delegate";
import NP, {EventKeyArgs, IHash} from "../../NP";

export default class TextField extends Control {

	public EvEnter: Delegate;
	public EvChanged: Delegate;
	public EvChanging: Delegate;
	public EvBlur: Delegate;
	public EvKeyDown: Delegate;
	public EvKeyPress: Delegate;
	public EvFocus: Delegate;
	public EvDeleteClick: Delegate;

	protected _EnableEdit: boolean;
	private _InputAttributes: IHash<string>;
	private _FontImage: string;
	private _FloatingLabel: string;
	private _EmptyLabel: boolean;
	private _Placeholder: string;
	private _FullWidth: boolean;
	private _MaxLength: object;
	private _IsPassword: boolean;
	private _ShowDeleteButton: boolean;

	protected _input: HTMLInputElement;
	protected _inputContainer: HTMLInputElement;

	constructor(settings: object)
	{
		super(settings);
		this._render();
	}

	_initFields(settings: object)
	{
		this._InputAttributes = {};
		this._FontImage = null;
		this._FloatingLabel = "";
		this._EmptyLabel = false;
		this._Placeholder = "";
		this._EnableEdit = true;
		this._FullWidth = false;
		this._MaxLength = null;
		this._ShowDeleteButton = false;
		this._IsPassword = false;
		this.EvEnter = new Delegate();
		this.EvChanged = new Delegate();
		this.EvBlur = new Delegate();
		this.EvKeyDown = new Delegate();
		this.EvKeyPress = new Delegate();
		this.EvFocus = new Delegate();
		this.EvChanging = new Delegate();
		this.EvDeleteClick = new Delegate();
		super._initFields(settings);
	}

	_onInputKeyDown(sender: Control, args: EventKeyArgs)
	{
		this.EvKeyDown.fire(this, args);
	}

	getFontImage()
	{
		return this._FontImage;
	}

	_render()
	{
		let tag = this._Class && this._Class.indexOf('text-field--textarea') > -1 ? 'textarea' : 'input',
			 maxLength = this._MaxLength ? `maxlength=${this._MaxLength}` : '',
			 label = this._FloatingLabel || this._EmptyLabel ? `<label for="${this._Id}" class="text-field__label">${this._FloatingLabel}</label>` : '';
			 //icon = this._FontImage ? `<i for="${this._Id}" class="text-field__icon app-list-item__graphic ${this._FontImage}"></i>` : '';

		this._initComponent(
			 `<div class="text-field">
					${label}
					<div class="text-field__input-container">
						
						<${tag} type="${this._IsPassword ? 'password' : 'text'}" ${maxLength}
							class="text-field__input ${tag === 'textarea' && 'text-field__input--textarea'}" id="${this._Id}"></${tag}>	
								  <div class="text-field__outline"></div>
						<div class="text-field__del-button-container">
							<div class="text-field__del-button ${NP.FontImages.cancel}"></div>
						</div>
					</div>
			</div>`);

		this._renderItems([this._root], this._ParentNode || document.documentElement);

		this._root.removeAttribute("id");

		if (this._FullWidth && (!NP.isNumber(this._Width) || this._Width <= 0))
			this._root.style.width = '100%';

		this._input = this._root.querySelector('.text-field__input');
		this._inputContainer = this._root.querySelector('.text-field__input-container');
		this.setEnableEdit(this._EnableEdit);

		let delButtonNode: HTMLElement = this._root.querySelector('.text-field__del-button');
		this.addEvent(delButtonNode, 'click', () =>
			 {
				 if (this._ShowDeleteButton && this._Enabled)
				 {
					 this.EvDeleteClick.fire(this);
				 }
			 }
		);

		if (this._Value || this._Content)
			this._input.value = this._Value || this._Content;

		if (this._Placeholder.length > 0)
			this._input.setAttribute('placeholder', this._Placeholder);

		//if (this._FontImage)
		//	NP.addCssClass(this._input, 'text-field__input--with-icon');

		Object.keys(this._InputAttributes).map(attr =>
		{
			if (Object.prototype.hasOwnProperty.call(this._InputAttributes, attr))
				this._input.setAttribute(attr, this._InputAttributes[attr]);
		});

		this._input.addEventListener('change', () => this.onFieldChange());
		this.addEvent(this._input, 'keydown', (s: HTMLElement, e: EventKeyArgs) => this._onInputKeyDown(this, e));
		this.addEvent(this._input, 'keypress', (s: HTMLElement, e: EventKeyArgs) => this._onInputKeyPress(this, e));

		this._input.addEventListener('keyup', (e: KeyboardEvent) => this.EvChanging.fire(this));
		this._input.addEventListener('blur', () =>
		{
			NP.removeCssClass(this._inputContainer, 'text-field__input-container--active');
			this.EvBlur.fire(this)
		});
		this._input.addEventListener('focus', () =>
		{
			NP.addCssClass(this._inputContainer, 'text-field__input-container--active');
			this.EvFocus.fire(this)
		});

		if (this._ShowDeleteButton)
			this.setShowDeleteButton(true);
	}

	getShowDeleteButton()
	{
		return this._ShowDeleteButton;
	}

	setShowDeleteButton(value: boolean)
	{
		this._ShowDeleteButton = value;
		let cls = "text-field--show-del";
		if (value)
			this.addClass(cls);
		else
			this.removeClass(cls);
	}

	setFloatingLabel(value: string)
	{
		this._FloatingLabel = value || '';
		(this._root.querySelector('.text-field__label') as HTMLElement).innerText = this._FloatingLabel;
	}

	_onInputKeyPress(sender: Control, args: EventKeyArgs)
	{
		if (this.isLive() && this._Enabled)
		{
			if (args.Event.keyCode == NP.Key.Enter)
				this.EvEnter.fire(this, args);
			return this.EvKeyPress.fire(sender, args);
		}
	}

	_getCaretPos(): number
	{
		return typeof this._input.selectionEnd === 'number' ? this._input.selectionEnd : 0;
	};

	getInput()
	{
		return this._input;
	}

	onFieldChange()
	{
		this.EvChanged.fire(this, {Content: this._input.value});
	}

	setEnableEdit(value: boolean)
	{
		this._EnableEdit = !!value;
		if (this._input)
		{
			if (this._EnableEdit)
				this._input.removeAttribute('readonly');
			else
				this._input.setAttribute('readonly', 'readonly');
		}
	}

	setEnabled(value: boolean, reInit?: boolean)
	{
		super.setEnabled(value, reInit);
		this.setEnableEdit(value);
	}

	select()
	{
		this._input.select();
	}

	focus()
	{
		this._input.focus();
	}

	setValue(value: string)
	{
		if (value == undefined)
			value = "";
		this._input.value = value;
	}

	getValue()
	{
		return this._input.value || '';
	}

	getTrimValue()
	{
		return (this._input.value || '').trim();
	}

	getContent()
	{
		return this._input.value;
	}

	setContent(content: string)
	{
		this.setValue(content);
	}
}